import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, type PlaceSuggestion } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../../components/ui/Button'
import { DateField, TimeField } from '../../components/ui/DatePicker'
import { Modal } from '../../components/ui/Modal'
import { PlaceAutocomplete } from '../../components/ui/PlaceAutocomplete'

interface Props {
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

function placeFromUser(u: ReturnType<typeof useSession.getState>['user']): PlaceSuggestion | null {
  if (!u || u.birthPlaceName == null || u.birthPlaceLat == null || u.birthPlaceLon == null) {
    return null
  }
  return {
    id: 'saved',
    name: u.birthPlaceName,
    displayName: u.birthPlaceName,
    country: null,
    lat: u.birthPlaceLat,
    lon: u.birthPlaceLon,
    timezone: u.timezone,
  }
}

export function BirthModal({ open, onClose, onSaved }: Props) {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const jwt = useSession((s) => s.jwt)
  const setSession = useSession((s) => s.setSession)
  const qc = useQueryClient()

  const [birthDate, setBirthDate] = useState(user?.birthDate ?? '')
  const [birthTime, setBirthTime] = useState(user?.birthTime ?? '')
  const [noTime, setNoTime] = useState(user?.birthDate != null && !user.birthTime)
  const [place, setPlace] = useState<PlaceSuggestion | null>(() => placeFromUser(user))

  // Re-sync form fields each time the modal is opened. Without this, useState
  // initialisers freeze on first mount — if the user object loaded *after* the
  // modal mounted (or place was added in a previous session), the form would
  // re-open with stale fields and a save could silently drop existing place
  // data because `place` was null at submit time.
  useEffect(() => {
    if (!open) return
    setBirthDate(user?.birthDate ?? '')
    setBirthTime(user?.birthTime ?? '')
    setNoTime(user?.birthDate != null && !user.birthTime)
    setPlace(placeFromUser(user))
  }, [open, user])

  const save = useMutation({
    mutationFn: () =>
      api.updateProfile({
        birthDate,
        birthTime: noTime ? null : birthTime || null,
        ...(place
          ? {
              birthPlaceLat: place.lat,
              birthPlaceLon: place.lon,
              birthPlaceName: place.name,
              timezone: place.timezone,
            }
          : {}),
      }),
    onSuccess: (updated) => {
      if (jwt) setSession({ user: updated, jwt })
      void qc.invalidateQueries()
      onSaved?.()
      onClose()
    },
  })

  const lang = (user?.language ?? 'ru') as 'ru' | 'en'

  return (
    <Modal open={open} onClose={onClose} title={t('birth_modal.title')}>
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('birth_modal.date')}</span>
          <DateField value={birthDate} onChange={setBirthDate} minAge={0} maxAge={100} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('birth_modal.time')}</span>
          <TimeField
            value={birthTime}
            onChange={(v) => { setBirthTime(v); setNoTime(false) }}
            disabled={noTime}
            minuteStep={5}
          />
          <label className="flex items-center gap-2 text-sm text-sage-600">
            <input
              type="checkbox"
              checked={noTime}
              onChange={(e) => setNoTime(e.target.checked)}
            />
            {t('birth_modal.no_time')}
          </label>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('birth_modal.place')}</span>
          <PlaceAutocomplete value={place} onChange={setPlace} language={lang} />
          <span className="text-xs text-sage-500">{t('birth_modal.place_hint')}</span>
        </label>

        {save.error && (
          <p className="text-sm text-copper">{(save.error as Error).message}</p>
        )}

        <Button
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)}
        >
          {t('actions.save')}
        </Button>
      </div>
    </Modal>
  )
}
